import * as apis from "@truckdown/jsclient/apis";
import { ILegacyListingService } from "./ILegacyListingService";
import { UpdateInfoModel } from "./UpdateInfoModel";
import { ForwardLocationModel } from "./ForwardLocationModel";
import { ApiResponseModel } from "./ApiResponseModel";
import { NoteModel } from "./NoteModel";
import { AddNoteModel } from "./AddNoteModel";
import { EditNoteModel } from "./EditNoteModel";
import { PreferenceModel } from "./PreferenceModel";
import { RatingNoteModel } from "./RatingNoteModel";
import { AddRatingModel } from "./AddRatingModel";

class LegacyListingService implements ILegacyListingService {
    constructor(framework: apis.framework.IFramework) {
        this.framework = framework;
        this.manager = framework.getRequiredService<apis.framework.IWebServiceManager>('IWebServiceManager');
        var alreadyHasService = framework.context.services.has(this.serviceName);

        if (!alreadyHasService) {
            this.setService(framework.context);
        }
    }

    private setService(context: apis.framework.IUserContext): void {
        var main = context.services.get(this.manager.main);
        if (main) {
            if (this.isLocal(main)) {
                context.services.set(this.serviceName, {
                    url: 'https://prototype.truckdown.com',
                    alt: 'http://prototype.truckdown.com'
                });
            } else {
                context.services.set(this.serviceName, main);
            }
        }
    }

    private isLocal(service: apis.framework.IWebService): boolean {
        if (service.url.indexOf('localhost') >= 0) {
            return true;
        }
        return false;
    }

    readonly framework: apis.framework.IFramework;
    readonly serviceName: string = 'legacy';
    readonly manager: apis.framework.IWebServiceManager;

    async updateInfo(model: UpdateInfoModel): Promise<boolean> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/listing/api/report-problem',
                method: apis.framework.HttpMethod.POST
            }, model);

            var res = await this.manager.send(req);
            return res.ok;
        } catch (err: unknown) {
            console.log('Exception thrown sending update info request.', err);
            return false;
        }
    }

    async forwardLocation(model: ForwardLocationModel): Promise<ApiResponseModel> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/listing/share',
                method: apis.framework.HttpMethod.POST
            }, model);

            return await this.manager.get<ApiResponseModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown forwarding a listing.', err);
            return {
                ok: false,
                cd: '400',
                msg: 'Error forwarding the listing'
            };
        }
    }

    async getNotes(id: string): Promise<NoteModel[]> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/notes/api/get-listing-notes/' + encodeURIComponent(id),
                method: apis.framework.HttpMethod.GET
            });

            return await this.manager.get<NoteModel[]>(req);
        } catch (err: unknown) {
            console.log('Exception thrown getting notes.', err);
            return [];
        }
    }

    async addNote(model: AddNoteModel): Promise<NoteModel | null> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/notes/api/create-note',
                method: apis.framework.HttpMethod.POST
            }, model);

            return await this.manager.get<NoteModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown adding a note.', err);
            return null;
        }
    }

    async editNote(model: EditNoteModel): Promise<NoteModel | null> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/notes/api/edit-note',
                method: apis.framework.HttpMethod.POST
            }, model);

            return await this.manager.get<NoteModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown editing a note.', err);
            return null;
        }
    }

    async deleteNote(id: string): Promise<ApiResponseModel> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/notes/api/delete-note/' + encodeURIComponent(id),
                method: apis.framework.HttpMethod.DELETE
            });

            var res = await this.manager.send(req);
            return { ok: res.ok, cd: res.status + '', msg: res.statusText };
        } catch (err: unknown) {
            console.log('Exception thrown deleting a note.', err);
            return {
                ok: false,
                cd: '400',
                msg: 'Error deleting the note'
            };
        }
    }

    async addPreferred(locationId: string, dnu: boolean): Promise<PreferenceModel | null> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/network/api/' + encodeURIComponent(locationId) + '?dnu=' + (dnu ? 'true' : 'false'),
                method: apis.framework.HttpMethod.PUT
            });

            return await this.manager.get<PreferenceModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown adding preferred.', err);
            return null;
        }
    }

    async editPreferred(model: PreferenceModel): Promise<PreferenceModel | null> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/network/api/' + encodeURIComponent(model.lid),
                method: apis.framework.HttpMethod.POST
            }, model);

            return await this.manager.get<PreferenceModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown adding preferred.', err);
            return null;
        }
    }

    async deletePreferred(locationId: string): Promise<ApiResponseModel> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/network/api/' + encodeURIComponent(locationId),
                method: apis.framework.HttpMethod.DELETE
            });

            var res = await this.manager.send(req);
            return { ok: res.ok, cd: res.status + '', msg: res.statusText };
        } catch (err: unknown) {
            console.log('Exception thrown deleting a preferred record.', err);
            return {
                ok: false,
                cd: '400',
                msg: 'Error removing preferred status'
            };
        }
    }

    async addRating(model: AddRatingModel): Promise<RatingNoteModel | null> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/ratings/api/create-rating',
                method: apis.framework.HttpMethod.POST
            }, model);

            return await this.manager.get<RatingNoteModel>(req);
        } catch (err: unknown) {
            console.log('Exception thrown adding rating.', err);
            return null;
        }
    }

    async canUpgradeLocation(accountId: string): Promise<boolean> {
        try {
            let req = this.manager.createRequest({
                service: this.serviceName,
                path: '/locations/upgrade-location-check/' + accountId,
                method: apis.framework.HttpMethod.GET
            });

            var res = await this.manager.send(req);
            return res.ok;
        } catch (err: unknown) {
            console.log('Exception thrown checking if location can be upgraded.', err);
            return false;
        }
    }
}

export default (framework: apis.framework.IFramework) => {
    framework.registerTransient<ILegacyListingService>('ILegacyListingService', (frm) => {
        return new LegacyListingService(frm);
    });
}